import { ChangeEvent } from "react";

import { Client } from "@/Common/Api/Client";
import * as LocalOperations from "@/Common/Operations/operations.local";
import * as ServerOperations from "@/Common/Operations/operations.server";
import { LocalEvent } from "@/Common/Stores/Actions/LocalEvent";

interface IPinControlProps {
    pinRequired: boolean;
    isAdminReset?: boolean;
    isAdminResetEmployeeId?: string;
    newPinText?: string;
    confirmedPinText?: string;
    pinSecurityText?: string;
    pinMismatchText?: string;
    hideAsterisk?: boolean;
    hideFooter?: boolean;
    onClosePopupCallBack?: () => void;
    onPinChange?: (pin: string, isPinValidated: boolean) => void;
}

class PinControlState {
    public pwdCheckPass: boolean;
    public pwdCheckDone: boolean;
    public currentPwd: string;
    public newPwd: string;
    public confirmedNewPwd: string;
    public inprogress: boolean;

    constructor(s?: PinControlState) {
        if (s != null) {
            this.pwdCheckPass = s.pwdCheckPass;
            this.pwdCheckDone = s.pwdCheckDone;
            this.currentPwd = s.currentPwd;
            this.newPwd = s.newPwd;
            this.confirmedNewPwd = s.confirmedNewPwd;
            this.inprogress = s.inprogress;
        }
        else {
            this.pwdCheckPass = false;
            this.pwdCheckDone = false;
            this.currentPwd = "";
            this.newPwd = "";
            this.confirmedNewPwd = "";
            this.inprogress = false;
        }
    }
}

class PinControl extends React.Component<IPinControlProps, PinControlState> {
    private client: Client;
    private requestPath: string;

    private readonly oldPinId = "__pinfcsbx001";
    private readonly newPinId = "__newpininputbox";

    constructor(props: IPinControlProps) {
        super(props);
        this.state = new PinControlState();
    }

    public componentDidUpdate(_prevProps: Readonly<IPinControlProps>, prevState: Readonly<PinControlState>): void {
        if (prevState.newPwd !== this.state.newPwd || prevState.confirmedNewPwd !== this.state.confirmedNewPwd) {
            if (this.props.onPinChange) {
                const isPinValidated =
                    !this.isPinMismatched(this.state.newPwd, this.state.confirmedNewPwd)
                    && !this.hasPinComplexityError(this.state.newPwd)
                    && this.state.newPwd.length > 0
                    && this.state.confirmedNewPwd.length > 0;

                this.props.onPinChange(isPinValidated ? this.state.confirmedNewPwd : "", isPinValidated);
            }
        }
    }

    public render(): JSX.Element {
        const hasPwdWarning = this.state.pwdCheckDone && !this.state.pwdCheckPass;

        const hasPwdMismatchWarning = this.isPinMismatched(this.state.newPwd, this.state.confirmedNewPwd);
        const hasPwdComplexityError = this.hasPinComplexityError(this.state.newPwd);

        let disableButton = hasPwdWarning || hasPwdMismatchWarning || hasPwdComplexityError || this.state.currentPwd.length === 0 || this.state.newPwd.length === 0 || this.state.confirmedNewPwd.length === 0 || this.state.inprogress;

        if (!this.props.pinRequired) {
            disableButton = false;
        }
        if (this.props.isAdminReset)
            disableButton = hasPwdMismatchWarning || hasPwdComplexityError || this.state.newPwd.length === 0 || this.state.confirmedNewPwd.length === 0 || this.state.inprogress;

        return (
            <div>
                <div className={this.props.isAdminReset ? "p-body" : ""}>
                    {this.props.pinRequired && (
                        <div className="row">
                            <div className="col-sm-4 col-md-4 col-lg-2">
                                <div className={this.buildErrorCssClass("form-group", hasPwdWarning)}>
                                    <label>{cacheStore.GetString("OldPINLabel")}{!this.props.hideAsterisk && "*"}</label>
                                    <input
                                        className="form-control"
                                        maxLength={4}
                                        minLength={4}
                                        type="password"
                                        id={this.oldPinId}
                                        onChange={this.onCurrentPwdChange.bind(this)}
                                        onFocus={this.OnPwdFocus.bind(this)}
                                        onKeyDown={this.HandleEscape.bind(this)}
                                        onBlur={this.ValidateInputPwd.bind(this)}
                                        value={this.state.currentPwd}
                                        autoComplete="off"
                                    />
                                    {hasPwdWarning && <small className="error">{cacheStore.GetString("InvalidPIN")}</small>}
                                </div>
                            </div>
                        </div>
                    )}
                    <div className="row">
                        <div className={this.props.isAdminReset ? "col-lg-10" : "col-sm-4 col-md-4 col-lg-2"}>
                            <div className={this.buildErrorCssClass("form-group", hasPwdComplexityError)}>
                                <label>{this.props.newPinText ?? cacheStore.GetString("AddNewPINLabel")}{!this.props.hideAsterisk && "*"}</label>
                                <input className="form-control" id={this.newPinId} maxLength={4} minLength={4} type="password" onChange={this.onNewPwdChange.bind(this)} value={this.state.newPwd} autoComplete="off" />
                                {hasPwdComplexityError && <small className="error">{this.props.pinSecurityText ?? cacheStore.GetString("PinSecurity")}</small>}
                            </div>
                        </div>
                    </div>
                    <div className="row">
                        <div className={this.props.isAdminReset ? "col-lg-10" : "col-sm-4 col-md-4 col-lg-2"}>
                            <div className={this.buildErrorCssClass("form-group", hasPwdMismatchWarning)} style={{ paddingTop: "10px" }}>
                                <label>{this.props.confirmedPinText ?? cacheStore.GetString("ConfirmLabel")}{!this.props.hideAsterisk && "*"}</label>
                                <input className="form-control" maxLength={4} minLength={4} type="password" onChange={this.onConfirmedPwdChange.bind(this)} value={this.state.confirmedNewPwd} autoComplete="off" />
                                {hasPwdMismatchWarning && <small className="error">{this.props.pinMismatchText ?? cacheStore.GetString("DifferentPinMessage")}</small>}
                            </div>
                        </div>
                    </div>
                </div>
                {!this.props.hideFooter && (
                    <div className={this.props.isAdminReset ? "p-footer p-footer-admin" : "row"} style={this.props.isAdminReset ? { "marginTop": "10px" } : {}}>
                        <div className={this.props.isAdminReset ? "text-right" : "col-sm-12 col-md-8 col-lg-3"}>
                            <button style={{ marginRight: "5px" }} onClick={this.onCancelClick.bind(this)} className="btn btn-default btn-sm">{cacheStore.GetString("CancelLabel")}</button>
                            <button className="btn btn-primary btn-sm" disabled={disableButton} onClick={this.onChangeClick.bind(this)}>{cacheStore.GetString("ChangeLabel")}</button>
                        </div>
                    </div>
                )}
            </div>
        );
    }

    private buildErrorCssClass(baseClass: string, isError: boolean): string { return isError ? baseClass + " has-error" : baseClass; }

    private onNewPwdChange(e: ChangeEvent<HTMLInputElement>): void {
        if (e.target != null) {
            const pwd: string = e.target.value.replace(/[^0-9]/g, "");
            this.setStateForNewPwd(pwd);
        }
    }

    private onCurrentPwdChange(e: ChangeEvent<HTMLInputElement>): void {
        const val = e.target.value.replace(/[^0-9]/g, "");
        this.setStateForCurrentPwd(val);
    }

    private onConfirmedPwdChange(e: ChangeEvent<HTMLInputElement>): void {
        const val = e.target.value.replace(/[^0-9]/g, "");
        this.setStateForConfirmedPwd(val);
    }

    private onCancelClick(): void {
        if (this.props.isAdminReset)
            this.props.onClosePopupCallBack();
        else
            sessionStore.dispatch(LocalOperations.SettingsUI.HidePINChange);
    }

    private onChangeClick(): void {
        if (this.state.inprogress) return;

        var self = this;
        let requestData;
        if (this.props.isAdminReset) {
            requestData = {
                IsAdminModify: true,
                UserId: this.props.isAdminResetEmployeeId,
                PIN: this.state.newPwd
            };

            this.client.Post(
                this.requestPath,
                ServerOperations.Server.Configuration_PINChange,
                requestData,
                response => {
                    var s2 = new PinControlState(self.state);
                    s2.inprogress = false;
                    self.setState(s2);
                    if (response.Success === true) {
                        window.setTimeout(
                            () => sessionStore.dispatch(LocalOperations.AdministrationUsers.CloseModifyPinPopup),
                            200
                        );
                    }
                },
                () => { }
            );
        }
        else {
            requestData = {
                UserId: sessionStore.userId,
                PIN: self.state.newPwd,
                OldPIN: self.state.currentPwd
            };

            var s = new PinControlState(this.state);
            s.inprogress = true;
            this.setState(s);

            this.client.Post(
                this.requestPath,
                ServerOperations.Server.Configuration_PINChange,
                requestData,
                response => {
                    var s2 = new PinControlState(self.state);
                    s2.inprogress = false;
                    self.setState(s2);

                    if (response.Success === true) {
                        window.setTimeout(
                            () => {
                                sessionStore.hasPinSet = true;

                                if (typeof settingsStore.configuration.get("SignatureByteArray") === "string" && settingsStore.configuration.get("SignatureByteArray").length > 0) {
                                    sessionStore.signOnPrintAccess = true;
                                }

                                var operation = new LocalEvent(LocalOperations.SettingsUI.HidePINChange);
                                LocalEventDispatcher.dispatch(operation);
                            },
                            200
                        );
                    }
                },
                () => { }
            );
        }
    }

    public componentDidMount(): void {
        window.setTimeout(() => {
            (this.props.isAdminReset) ? $("#" + this.newPinId).focus() : $("#" + this.oldPinId).focus();
        }, 200);

        this.client = new Client();
        this.requestPath = apiSettings.buildApiPath();
    }

    private HandleEscape(e: React.KeyboardEvent): void {
        if (e.keyCode === 27) {
            var operation = new LocalEvent(LocalOperations.SettingsUI.HidePINChange);
            LocalEventDispatcher.dispatch(operation);
        }
    }

    private ValidateInputPwd(e: React.FocusEvent<HTMLInputElement>): void {
        var pwd: string = e.target.value;
        if (pwd.length > 0) {
            var self = this;
            this.client.Post(
                this.requestPath,
                ServerOperations.Server.Configuration_Validate_PIN,
                { UserId: sessionStore.userId, PIN: pwd },
                response => self.setStateForPwdCheck(response.Valid, self),
                () => { }
            );
        }
    }

    private OnPwdFocus(): void {
        this.resetStateForPwdCheck();
    }

    private setStateForPwdCheck(success: boolean, reference: PinControl): void {
        var s = new PinControlState(this.state);
        s.pwdCheckPass = success;
        s.pwdCheckDone = true;
        reference.setState(s);
    }

    private resetStateForPwdCheck(): void {
        var s = new PinControlState(this.state);
        s.pwdCheckPass = false;
        s.pwdCheckDone = false;
        this.setState(s);
    }

    private setStateForNewPwd(value: string): void {
        var s = new PinControlState(this.state);
        s.newPwd = value;
        this.setState(s);
    }

    private setStateForCurrentPwd(value: string): void {
        var s = new PinControlState(this.state);
        s.currentPwd = value;
        this.setState(s);
    }

    private setStateForConfirmedPwd(value: string): void {
        var s = new PinControlState(this.state);
        s.confirmedNewPwd = value;
        this.setState(s);
    }

    public shouldComponentUpdate(): boolean {
        return true;
    }

    private hasPinComplexityError(pin: string): boolean {
        const exp = /^[0-9]{4}$/;
        return !exp.test(pin) && pin.length > 0;
    }

    private isPinMismatched(pinA: string, pinB: string): boolean {
        return pinA !== pinB && pinA.length > 0 && pinB.length > 0;
    }
}

export { PinControl };
